import React, { useEffect, useState } from 'react';
import { supabase, Profile, Certification, InsurancePolicy, TaxDocument, maskTaxId } from '../../lib/supabase';
import { TradeBadge } from '../shared/TradeBadge';
import {
  X,
  User,
  MapPin,
  Phone,
  Briefcase,
  CheckCircle2,
  Clock,
  Shield,
  FileText,
  Calendar,
  AlertCircle,
  MessageSquare,
  Star,
  TrendingUp
} from 'lucide-react';

interface ContractorProfileModalProps {
  contractor: Profile;
  onClose: () => void;
}

export const ContractorProfileModal: React.FC<ContractorProfileModalProps> = ({
  contractor,
  onClose
}) => {
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [insurance, setInsurance] = useState<InsurancePolicy[]>([]);
  const [taxDoc, setTaxDoc] = useState<TaxDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'certifications' | 'insurance' | 'compliance'>('overview');

  useEffect(() => {
    loadContractorDetails();
  }, [contractor.id]);

  const loadContractorDetails = async () => {
    try {
      const [certsRes, insuranceRes, taxRes] = await Promise.all([
        supabase
          .from('certifications')
          .select('*')
          .eq('user_id', contractor.id)
          .order('created_at', { ascending: false }),
        supabase
          .from('insurance_policies')
          .select('*')
          .eq('user_id', contractor.id)
          .order('expiry_date', { ascending: true }),
        supabase
          .from('tax_documents')
          .select('*')
          .eq('user_id', contractor.id)
          .maybeSingle()
      ]);

      setCertifications(certsRes.data || []);
      setInsurance(insuranceRes.data || []);
      if (taxRes.error && taxRes.error.code !== 'PGRST116') {
        console.error('Tax doc error:', taxRes.error);
      } else {
        setTaxDoc(taxRes.data);
      }
    } catch (error) {
      console.error('Error loading contractor details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getVerificationBadge = () => {
    switch (contractor.verification_status) {
      case 'verified':
        return (
          <div className="flex items-center space-x-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-full border border-green-200">
            <CheckCircle2 className="w-4 h-4" />
            <span className="text-sm font-medium">Verified</span>
          </div>
        );
      case 'pending':
        return (
          <div className="flex items-center space-x-2 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full border border-amber-200">
            <Clock className="w-4 h-4" />
            <span className="text-sm font-medium">Pending</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center space-x-2 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-full border border-slate-200">
            <span className="text-sm font-medium">Not Verified</span>
          </div>
        );
    }
  };

  const getInsuranceStatus = (policy: InsurancePolicy) => {
    const daysUntilExpiry = Math.ceil(
      (new Date(policy.expiry_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysUntilExpiry < 0) {
      return { text: 'Expired', color: 'text-red-600 bg-red-50 border-red-200', icon: AlertCircle };
    }
    if (daysUntilExpiry < 30) {
      return { text: `${daysUntilExpiry} days left`, color: 'text-amber-600 bg-amber-50 border-amber-200', icon: Clock };
    }
    return { text: 'Active', color: 'text-green-600 bg-green-50 border-green-200', icon: CheckCircle2 };
  };

  const mockJobHistory = [
    { project: 'Residential Framing', gc: 'BuildCo LLC', date: '2024-01-15', rating: 5 },
    { project: 'Commercial Roofing', gc: 'Metro Construction', date: '2023-12-10', rating: 4.5 },
    { project: 'Kitchen Remodel', gc: 'HomeWorks Inc', date: '2023-11-05', rating: 5 },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60] overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[95vh] overflow-hidden flex flex-col my-4">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold text-slate-900">Contractor Profile</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            <div className="bg-gradient-to-r from-slate-700 to-slate-900 rounded-xl p-6 mb-6 -mt-2">
              <div className="flex items-start gap-4">
                <div className="w-20 h-20 rounded-xl bg-white shadow-lg flex items-center justify-center flex-shrink-0">
                  <User className="w-10 h-10 text-slate-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-2xl font-bold text-white">{contractor.full_name}</h3>
                    {getVerificationBadge()}
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {contractor.trades && contractor.trades.length > 0 ? (
                      contractor.trades.map((trade) => (
                        <TradeBadge key={trade} trade={trade} size="md" />
                      ))
                    ) : contractor.trade_type ? (
                      <TradeBadge trade={contractor.trade_type} size="md" />
                    ) : null}
                  </div>
                  <div className="flex items-center gap-6 text-white/90 text-sm">
                    <div className="flex items-center">
                      <Briefcase className="w-4 h-4 mr-1.5" />
                      {contractor.jobs_completed} jobs
                    </div>
                    <div className="flex items-center">
                      <Star className="w-4 h-4 mr-1.5 text-yellow-400" />
                      {contractor.rating.toFixed(1)} rating
                    </div>
                    {contractor.location && (
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-1.5" />
                        {contractor.location}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="border-b border-slate-200 mb-6">
              <div className="flex gap-1">
                {(['overview', 'certifications', 'insurance', 'compliance'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-6 py-3 font-medium transition-all capitalize ${
                      activeTab === tab
                        ? 'text-slate-900 border-b-2 border-slate-900'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
              </div>
            ) : (
              <>
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <Briefcase className="w-8 h-8 text-slate-600" />
                          <TrendingUp className="w-4 h-4 text-green-600" />
                        </div>
                        <div className="text-2xl font-bold text-slate-900">{contractor.jobs_completed}</div>
                        <div className="text-sm text-slate-600">Jobs Completed</div>
                      </div>

                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <Star className="w-8 h-8 text-amber-500" />
                        </div>
                        <div className="text-2xl font-bold text-slate-900">{contractor.rating.toFixed(1)}</div>
                        <div className="text-sm text-slate-600">Average Rating</div>
                      </div>

                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <Shield className="w-8 h-8 text-amber-600" />
                        </div>
                        <div className="text-2xl font-bold text-slate-900">{insurance.filter(i => i.status === 'active').length}</div>
                        <div className="text-sm text-slate-600">Active Policies</div>
                      </div>
                    </div>

                    {contractor.bio && (
                      <div className="bg-white rounded-xl border border-slate-200 p-6">
                        <h3 className="font-semibold text-slate-900 mb-3">About</h3>
                        <p className="text-slate-600 leading-relaxed">{contractor.bio}</p>
                      </div>
                    )}

                    <div className="bg-white rounded-xl border border-slate-200 p-6">
                      <h3 className="font-semibold text-slate-900 mb-4">Contact Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {contractor.phone && (
                          <div className="flex items-center text-slate-700">
                            <Phone className="w-4 h-4 mr-3 text-slate-400" />
                            <div>
                              <div className="text-xs text-slate-500 mb-0.5">Phone</div>
                              <div className="text-sm font-medium">{contractor.phone}</div>
                            </div>
                          </div>
                        )}
                        {contractor.company_name && (
                          <div className="flex items-center text-slate-700">
                            <Briefcase className="w-4 h-4 mr-3 text-slate-400" />
                            <div>
                              <div className="text-xs text-slate-500 mb-0.5">Company</div>
                              <div className="text-sm font-medium">{contractor.company_name}</div>
                            </div>
                          </div>
                        )}
                        {contractor.location && (
                          <div className="flex items-center text-slate-700">
                            <MapPin className="w-4 h-4 mr-3 text-slate-400" />
                            <div>
                              <div className="text-xs text-slate-500 mb-0.5">Location</div>
                              <div className="text-sm font-medium">{contractor.location}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="bg-white rounded-xl border border-slate-200 p-6">
                      <h3 className="font-semibold text-slate-900 mb-4">Recent Jobs</h3>
                      <div className="space-y-3">
                        {mockJobHistory.map((job, idx) => (
                          <div key={idx} className="flex items-start justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex-1">
                              <div className="font-medium text-slate-900">{job.project}</div>
                              <div className="text-sm text-slate-600">{job.gc}</div>
                              <div className="text-xs text-slate-500 mt-1">
                                {new Date(job.date).toLocaleDateString()}
                              </div>
                            </div>
                            <div className="flex items-center text-amber-500">
                              <Star className="w-4 h-4 fill-current mr-1" />
                              <span className="font-semibold">{job.rating}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'certifications' && (
                  <div className="space-y-4">
                    {certifications.length === 0 ? (
                      <div className="text-center py-12 bg-slate-50 rounded-xl">
                        <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-600">No certifications on file</p>
                      </div>
                    ) : (
                      certifications.map((cert) => (
                        <div key={cert.id} className="bg-white border border-slate-200 rounded-xl p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold text-slate-900">{cert.name}</h4>
                                {cert.verified && (
                                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                                )}
                              </div>
                              {cert.issuer && (
                                <p className="text-sm text-slate-600 mb-2">{cert.issuer}</p>
                              )}
                              {cert.number && (
                                <p className="text-xs text-slate-500">License #: {cert.number}</p>
                              )}
                            </div>
                            <span className={`px-3 py-1 text-xs font-medium rounded-full capitalize ${
                              cert.type === 'license'
                                ? 'bg-amber-100 text-amber-700'
                                : cert.type === 'certification'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-slate-100 text-slate-700'
                            }`}>
                              {cert.type}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                            {cert.issue_date && (
                              <div>
                                <div className="text-xs text-slate-500 mb-1">Issued</div>
                                <div className="text-sm font-medium text-slate-900">
                                  {new Date(cert.issue_date).toLocaleDateString()}
                                </div>
                              </div>
                            )}
                            {cert.expiry_date && (
                              <div>
                                <div className="text-xs text-slate-500 mb-1">Expires</div>
                                <div className="flex items-center text-sm font-medium">
                                  <Calendar className="w-3 h-3 mr-1 text-slate-400" />
                                  {new Date(cert.expiry_date).toLocaleDateString()}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {activeTab === 'compliance' && (
                  <div className="space-y-6">
                    <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl border border-blue-200 p-6">
                      <h3 className="font-bold text-slate-900 mb-4">Compliance Summary</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className={`flex items-center space-x-3 p-4 rounded-lg border-2 ${
                          insurance.some(p => p.policy_type === 'liability')
                            ? 'bg-green-50 border-green-200'
                            : 'bg-slate-50 border-slate-200'
                        }`}>
                          <Shield className={`w-8 h-8 ${
                            insurance.some(p => p.policy_type === 'liability')
                              ? 'text-green-600'
                              : 'text-slate-400'
                          }`} />
                          <div className="flex-1">
                            <div className="font-medium text-slate-900">General Liability</div>
                            {insurance.find(p => p.policy_type === 'liability') ? (
                              <div className="text-sm text-slate-600">
                                Exp: {new Date(insurance.find(p => p.policy_type === 'liability')!.expiry_date).toLocaleDateString()}
                              </div>
                            ) : (
                              <div className="text-sm text-slate-500">Not provided</div>
                            )}
                          </div>
                          {insurance.some(p => p.policy_type === 'liability') && (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          )}
                        </div>

                        <div className={`flex items-center space-x-3 p-4 rounded-lg border-2 ${
                          insurance.some(p => p.policy_type === 'workers_comp')
                            ? 'bg-green-50 border-green-200'
                            : 'bg-slate-50 border-slate-200'
                        }`}>
                          <Shield className={`w-8 h-8 ${
                            insurance.some(p => p.policy_type === 'workers_comp')
                              ? 'text-green-600'
                              : 'text-slate-400'
                          }`} />
                          <div className="flex-1">
                            <div className="font-medium text-slate-900">Workers Comp</div>
                            {insurance.find(p => p.policy_type === 'workers_comp') ? (
                              <div className="text-sm text-slate-600">
                                Exp: {new Date(insurance.find(p => p.policy_type === 'workers_comp')!.expiry_date).toLocaleDateString()}
                              </div>
                            ) : (
                              <div className="text-sm text-slate-500">Not provided</div>
                            )}
                          </div>
                          {insurance.some(p => p.policy_type === 'workers_comp') && (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          )}
                        </div>

                        <div className={`flex items-center space-x-3 p-4 rounded-lg border-2 ${
                          taxDoc?.w9_document_url
                            ? taxDoc.w9_verified
                              ? 'bg-green-50 border-green-200'
                              : 'bg-amber-50 border-amber-200'
                            : 'bg-slate-50 border-slate-200'
                        }`}>
                          <FileText className={`w-8 h-8 ${
                            taxDoc?.w9_document_url
                              ? taxDoc.w9_verified
                                ? 'text-green-600'
                                : 'text-amber-600'
                              : 'text-slate-400'
                          }`} />
                          <div className="flex-1">
                            <div className="font-medium text-slate-900">W-9 Form</div>
                            {taxDoc?.w9_document_url ? (
                              <div className="text-sm text-slate-600">
                                {taxDoc.w9_verified ? 'Verified' : 'Pending verification'}
                              </div>
                            ) : (
                              <div className="text-sm text-slate-500">Not uploaded</div>
                            )}
                          </div>
                          {taxDoc?.w9_verified && (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          )}
                        </div>

                        <div className={`flex items-center space-x-3 p-4 rounded-lg border-2 ${
                          taxDoc?.tax_id
                            ? 'bg-green-50 border-green-200'
                            : 'bg-slate-50 border-slate-200'
                        }`}>
                          <Shield className={`w-8 h-8 ${
                            taxDoc?.tax_id ? 'text-green-600' : 'text-slate-400'
                          }`} />
                          <div className="flex-1">
                            <div className="font-medium text-slate-900">Tax ID</div>
                            {taxDoc?.tax_id ? (
                              <div className="text-sm text-slate-600 font-mono">
                                {maskTaxId(taxDoc.tax_id)}
                              </div>
                            ) : (
                              <div className="text-sm text-slate-500">Not provided</div>
                            )}
                          </div>
                          {taxDoc?.tax_id && (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          )}
                        </div>
                      </div>
                    </div>

                    {taxDoc && (taxDoc.business_legal_name || taxDoc.tax_classification) && (
                      <div className="bg-white rounded-xl border border-slate-200 p-6">
                        <h3 className="font-bold text-slate-900 mb-4">Business Information</h3>
                        <div className="space-y-3">
                          {taxDoc.business_legal_name && (
                            <div className="flex items-start">
                              <div className="w-32 text-sm text-slate-600">Legal Name</div>
                              <div className="flex-1 text-sm font-medium text-slate-900">
                                {taxDoc.business_legal_name}
                              </div>
                            </div>
                          )}
                          {taxDoc.tax_classification && (
                            <div className="flex items-start">
                              <div className="w-32 text-sm text-slate-600">Classification</div>
                              <div className="flex-1 text-sm font-medium text-slate-900 capitalize">
                                {taxDoc.tax_classification.replace('_', ' ')}
                              </div>
                            </div>
                          )}
                          {taxDoc.w9_submission_date && (
                            <div className="flex items-start">
                              <div className="w-32 text-sm text-slate-600">W-9 Submitted</div>
                              <div className="flex-1 text-sm font-medium text-slate-900">
                                {new Date(taxDoc.w9_submission_date).toLocaleDateString()}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'insurance' && (
                  <div className="space-y-4">
                    {insurance.length === 0 ? (
                      <div className="text-center py-12 bg-slate-50 rounded-xl">
                        <Shield className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-600">No insurance policies on file</p>
                      </div>
                    ) : (
                      insurance.map((policy) => {
                        const statusInfo = getInsuranceStatus(policy);
                        const StatusIcon = statusInfo.icon;
                        return (
                          <div key={policy.id} className="bg-white border border-slate-200 rounded-xl p-6">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h4 className="font-semibold text-slate-900 capitalize">
                                    {policy.policy_type.replace('_', ' ')} Insurance
                                  </h4>
                                  <div className={`flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium border ${statusInfo.color}`}>
                                    <StatusIcon className="w-3 h-3" />
                                    <span>{statusInfo.text}</span>
                                  </div>
                                </div>
                                <p className="text-sm text-slate-600">{policy.provider}</p>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100">
                              <div>
                                <div className="text-xs text-slate-500 mb-1">Policy Number</div>
                                <div className="text-sm font-medium text-slate-900">
                                  {policy.policy_number || 'N/A'}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs text-slate-500 mb-1">Coverage</div>
                                <div className="text-sm font-medium text-slate-900">
                                  ${(policy.coverage_amount || 0).toLocaleString()}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs text-slate-500 mb-1">Start Date</div>
                                <div className="text-sm font-medium text-slate-900">
                                  {new Date(policy.start_date).toLocaleDateString()}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs text-slate-500 mb-1">Expires</div>
                                <div className="text-sm font-medium text-slate-900">
                                  {new Date(policy.expiry_date).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </>
            )}

            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="flex gap-3">
                <button className="flex-1 px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-xl transition-colors flex items-center justify-center">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Send Message
                </button>
                <button className="px-6 py-3 border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-colors">
                  Add to Roster
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
